import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { RecipientsDisplayComponent } from "../recipients-display/recipients-display.component";
import { User } from 'src/app/models/user.model';

@Component({
    selector: 'que-autocomplete-filter',
    templateUrl: './autocomplete-filter.component.html',
    styleUrls: ['./autocomplete-filter.component.scss'],
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: AutoCompleteFilterComponent, multi: true }],
    imports: [
        CommonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        FormsModule,
        MatCheckboxModule,
        TranslateDirective, TranslatePipe,
        RecipientsDisplayComponent
    ]
})
export class AutoCompleteFilterComponent implements ControlValueAccessor, OnChanges {
  isopenSelect: boolean = false;
  // flag: boolean | undefined = false;
  isSelectAll: boolean = false;

  // Recomputes each option's checked state whenever either input changes - not just once at
  // construction. autoCompleteList is often populated asynchronously (e.g. a name list fetched from
  // the server after this component is already rendered), so a one-time ngOnInit computation missed
  // any selection that was already applied by the time the list actually arrived: the filter itself
  // still worked (the value was applied), but the checkboxes rendered as unchecked.
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['autoCompleteList'] && !changes['selected']) return;

    const selectedIds = this.selected ?? [];
    this.autoCompleteList = this.autoCompleteList?.map(value => ({
      ...value,
      completed: selectedIds.includes(value.id)
    }));
    this.list$.next(this.autoCompleteList);
  }

  @Input() autoCompleteList: User[] | undefined | null;
  @Input() selected?: number[] | undefined | null = [];
  readonly valueControl = new FormControl<number[]>([]);
  readonly searchControl = new FormControl<string | User>('');
  callBackFunction: Function = () => { };

  // Emits whenever autoCompleteList is rebuilt, so filteredOptions re-renders from the current
  // objects. Without it the rendered options stayed the objects captured on the first emission,
  // while apply() read the rebuilt list: after the first click (which updates `selected` and so
  // rebuilds the list), every further tick toggled a stale object apply() never saw - only the
  // first selection ever reached the filter.
  private readonly list$ = new BehaviorSubject<User[] | undefined | null>(undefined);

  readonly filteredOptions = combineLatest([
    this.searchControl.valueChanges.pipe(startWith('')),
    this.list$
  ]).pipe(
    map(([value]) => {
      const name: string = typeof value === 'string' ? value : `${value?.id} ${value?.firstName} ${value?.lastName}`;
      return name ? this._filter(name) : this.autoCompleteList?.slice();
    })
  );

  apply() {
    const selectedArr = this.autoCompleteList?.filter(x => x.completed);
    this.valueControl.setValue(selectedArr ? selectedArr.map(item => item.id!) : [])
    if (this.callBackFunction) {
      this.callBackFunction(this.valueControl.value);
      // this.autoCompleteList = this.autoCompleteList?.map(value => { return { ...value, completed: false } })
    }
  }

  selectAll(): void {
    this.isSelectAll = !this.isSelectAll;
    if (this.isSelectAll) {
      this.autoCompleteList = this.autoCompleteList?.map(value => {
        return { ...value, completed : true };
      });
      this.list$.next(this.autoCompleteList);
      // this.flag = this.autoCompleteList?.some(x => x.completed);
    }
  }

  writeValue(obj: any): void {
    this.valueControl.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.callBackFunction = fn;
  }

  registerOnTouched(fn: any): void {
    // throw new Error('Method not implemented.');
  }

  selectOpened() {
    this.isopenSelect = true;
  }

  selectClosed() {
    this.isopenSelect = false;
  }

  requireMatch(control: AbstractControl) {
    const selection: any = control.value;
    if (typeof selection === 'string') {
      return { incorrect: true };
    }
    return null;
  }

  private _filter(name: string): User[] | undefined {
    const filterValue = name.toLowerCase();
    return this.autoCompleteList?.filter(option => option.firstName.toLowerCase().includes(filterValue) || option.lastName?.toLowerCase().includes(filterValue) || option.id.toString().includes(filterValue));
  }

  addItem(e: Event, option: User) {
    e.stopPropagation();
    option.completed = !option.completed;
    this.apply()
    // this.flag = this.autoCompleteList?.some(x => x.completed);
  }

}
